/* ==============================================================================
   Snowflake — IDs de 64 bits ordenáveis por tempo
   ==============================================================================
   Usado só em `app.messages`, a tabela mais quente do sistema. Todo o resto usa
   uuid. Três motivos concretos para a exceção:

   1. **Paginação por cursor sem coluna extra.** `WHERE channel_id = $1 AND
      id < $2 ORDER BY id DESC` usa um índice só. Com uuid v4 seria preciso
      ordenar por `created_at`, que não é único — e paginação sobre chave não
      única perde ou duplica linhas na fronteira das páginas.
   2. **Índice compacto.** bigint são 8 bytes contra 16 do uuid, e os valores
      são sequenciais, então as páginas do B-tree enchem em ordem em vez de
      fragmentar. Na tabela que mais cresce, isso é a diferença entre o índice
      caber ou não em memória.
   3. **ID gerado no cliente.** O cliente cria o ID antes de enviar, renderiza a
      mensagem na hora, e quando o servidor confirma não há reconciliação — o ID
      já é o definitivo. Sem isso, a UI otimista precisa de ID temporário e de
      um passo de troca que produz piscada e bugs de ordenação.

   Layout (63 bits, cabe em bigint com sinal do Postgres):

       ┌───────────────────────────────┬──────────┬────────────┐
       │ 41 bits  ms desde a época     │ 10 bits  │ 12 bits    │
       │          customizada          │ worker   │ sequência  │
       └───────────────────────────────┴──────────┴────────────┘

   41 bits de milissegundo ≈ 69 anos a partir da época. 12 bits de sequência
   permitem 4096 IDs por milissegundo por worker — bem acima de qualquer carga
   realista deste produto.
   ============================================================================== */

/**
 * Época customizada: 2026-01-01T00:00:00Z.
 *
 * Época própria em vez da Unix porque os 41 bits contam a partir dela. Com a
 * época Unix, metade da faixa já estaria gasta em 2026 e o esquema duraria ~13
 * anos em vez de 69.
 *
 * **Nunca mude este valor num sistema com dados.** Todos os IDs existentes
 * passariam a decodificar para um instante diferente, e a ordenação entre IDs
 * antigos e novos quebraria.
 */
export const SNOWFLAKE_EPOCH_MS = 1_767_225_600_000;

const TIMESTAMP_BITS = 41n;
const WORKER_BITS = 10n;
const SEQUENCE_BITS = 12n;

const MAX_WORKER = (1 << Number(WORKER_BITS)) - 1; // 1023
const MAX_SEQUENCE = (1 << Number(SEQUENCE_BITS)) - 1; // 4095

const WORKER_SHIFT = SEQUENCE_BITS;
const TIMESTAMP_SHIFT = SEQUENCE_BITS + WORKER_BITS;

const SEQUENCE_MASK = (1n << SEQUENCE_BITS) - 1n;
const WORKER_MASK = (1n << WORKER_BITS) - 1n;
const TIMESTAMP_MASK = (1n << TIMESTAMP_BITS) - 1n;

export interface SnowflakeParts {
  timestamp: Date;
  workerId: number;
  sequence: number;
}

export interface SnowflakeGeneratorOptions {
  /**
   * Identifica quem gera. Cada processo de servidor e cada cliente conectado
   * precisa de um valor distinto, senão dois podem emitir o mesmo ID no mesmo
   * milissegundo com a mesma sequência.
   */
  workerId: number;
  /** Injetável para teste. Default: `Date.now`. */
  now?: () => number;
}

export class SnowflakeGenerator {
  private readonly workerId: bigint;
  private readonly now: () => number;
  private lastTimestamp = -1;
  private sequence = 0;

  constructor(options: SnowflakeGeneratorOptions) {
    if (
      !Number.isInteger(options.workerId) ||
      options.workerId < 0 ||
      options.workerId > MAX_WORKER
    ) {
      throw new RangeError(
        `workerId deve ser inteiro entre 0 e ${MAX_WORKER}, recebido ${options.workerId}`,
      );
    }
    this.workerId = BigInt(options.workerId);
    this.now = options.now ?? Date.now;
  }

  /** Gera o próximo ID. Monotônico dentro deste gerador. */
  next(): bigint {
    let timestamp = this.now() - SNOWFLAKE_EPOCH_MS;

    if (timestamp < 0) {
      throw new RangeError(
        'Relógio anterior à época do snowflake (2026-01-01). Verifique o relógio do sistema.',
      );
    }

    if (timestamp === this.lastTimestamp) {
      this.sequence = (this.sequence + 1) & MAX_SEQUENCE;
      if (this.sequence === 0) {
        // 4096 IDs no mesmo milissegundo: espera o próximo em vez de reusar
        // sequência, que produziria ID duplicado.
        timestamp = this.waitNextMillis(this.lastTimestamp);
      }
    } else if (timestamp < this.lastTimestamp) {
      /*
       * Relógio andou para trás — acontece de verdade com ajuste de NTP, e é
       * exatamente onde implementações ingênuas emitem ID duplicado.
       *
       * A escolha aqui é continuar no último milissegundo conhecido e consumir
       * sequência, em vez de lançar erro. Motivo: uma mensagem não deve falhar
       * porque o NTP corrigiu 3 ms. O ID fica com timestamp levemente à frente
       * do relógio real, o que é preferível a perder monotonicidade — que
       * quebraria a paginação por cursor.
       */
      timestamp = this.lastTimestamp;
      this.sequence = (this.sequence + 1) & MAX_SEQUENCE;
      if (this.sequence === 0) {
        timestamp = this.waitNextMillis(this.lastTimestamp);
      }
    } else {
      this.sequence = 0;
    }

    this.lastTimestamp = timestamp;

    return (
      ((BigInt(timestamp) & TIMESTAMP_MASK) << TIMESTAMP_SHIFT) |
      (this.workerId << WORKER_SHIFT) |
      BigInt(this.sequence)
    );
  }

  /** Conveniência: a API transporta snowflake como string (ver `toJSON`). */
  nextString(): string {
    return this.next().toString();
  }

  private waitNextMillis(last: number): number {
    let current = this.now() - SNOWFLAKE_EPOCH_MS;

    /*
     * Espera ocupada até o milissegundo virar. Normalmente dura microssegundos
     * e só é alcançada acima de 4096 IDs/ms num worker.
     *
     * O limite de iterações NÃO é paranoia: sem ele, um relógio parado (VM
     * suspensa, `now` injetado que não avança, timer do SO travado) prende o
     * processo em laço infinito, sem log e sem timeout. Um servidor que travou
     * é pior que um que devolveu erro — o travado não aparece em métrica de
     * erro e o healthcheck demora a matá-lo.
     *
     * 50 milhões de iterações leva bem menos de um segundo em hardware atual,
     * então nenhum caso legítimo chega perto disto.
     */
    const MAX_SPIN = 50_000_000;
    let spins = 0;

    while (current <= last) {
      if (++spins > MAX_SPIN) {
        throw new Error(
          `Relógio parado em ${current + SNOWFLAKE_EPOCH_MS}: a sequência de ` +
            `snowflake esgotou (4096/ms) e o milissegundo não avançou após ` +
            `${MAX_SPIN} tentativas. Verifique o relógio do sistema.`,
        );
      }
      current = this.now() - SNOWFLAKE_EPOCH_MS;
    }

    this.sequence = 0;
    return current;
  }
}

/* ------------------------------------------------------------------------------
   Decodificação
------------------------------------------------------------------------------ */

export function parseSnowflake(id: bigint | string): SnowflakeParts {
  const value = typeof id === 'string' ? BigInt(id) : id;
  if (value < 0n) {
    throw new RangeError(`Snowflake não pode ser negativo: ${value}`);
  }
  return {
    timestamp: new Date(Number((value >> TIMESTAMP_SHIFT) & TIMESTAMP_MASK) + SNOWFLAKE_EPOCH_MS),
    workerId: Number((value >> WORKER_SHIFT) & WORKER_MASK),
    sequence: Number(value & SEQUENCE_MASK),
  };
}

/** Só o instante. Evita alocar o objeto completo quando é o único dado usado. */
export function snowflakeToDate(id: bigint | string): Date {
  const value = typeof id === 'string' ? BigInt(id) : id;
  return new Date(Number((value >> TIMESTAMP_SHIFT) & TIMESTAMP_MASK) + SNOWFLAKE_EPOCH_MS);
}

/**
 * Menor snowflake possível para um instante, com worker e sequência zerados.
 *
 * Serve para converter filtro por data em filtro por chave primária:
 * `WHERE id >= snowflakeForDate(inicio)` usa o índice
 * `messages(channel_id, id DESC)` em vez de varrer por `created_at`.
 */
export function snowflakeForDate(date: Date): bigint {
  const delta = date.getTime() - SNOWFLAKE_EPOCH_MS;
  if (delta < 0) return 0n;
  return (BigInt(delta) & TIMESTAMP_MASK) << TIMESTAMP_SHIFT;
}

const SNOWFLAKE_PATTERN = /^\d{1,19}$/;

/** Valida a forma textual, sem lançar. Para entrada não confiável da API. */
export function isSnowflake(value: unknown): value is string {
  if (typeof value !== 'string' || !SNOWFLAKE_PATTERN.test(value)) return false;
  try {
    const parsed = BigInt(value);
    // Cabe em 63 bits (bigint com sinal do Postgres).
    return parsed >= 0n && parsed < 1n << 63n;
  } catch {
    return false;
  }
}

/**
 * Snowflake é serializado como STRING no JSON.
 *
 * Obrigatório: `JSON.stringify` não sabe serializar BigInt (lança TypeError), e
 * se fosse convertido para `number` o valor passaria de 2^53 e perderia
 * precisão — a mensagem apontaria para outro ID. Este é o mesmo motivo de
 * permissões serem string na API.
 */
export function serializeSnowflake(id: bigint): string {
  return id.toString();
}
